import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useLayoutEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navItems = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.services"), path: "/services" },
    { label: t("nav.about"), path: "/about" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  const toggleLang = () => {
    const next = i18n.language === "en" ? "hu" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  };

  const isHome = location.pathname === "/";

  useLayoutEffect(() => {
    if (!isHome) {
      setScrolledPastHero(true);
      return;
    }
    setScrolledPastHero(window.scrollY > window.innerHeight * 0.8);
  }, [isHome]);

  useEffect(() => {
    if (!isHome) return;
    const handleScroll = () => {
      setScrolledPastHero(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const isTransparent = isHome && !scrolledPastHero;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isTransparent && !menuOpen ? "bg-transparent" : !menuOpen ? "bg-background/95 backdrop-blur-sm border-b border-border" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none" onClick={() => setMenuOpen(false)}>
          <span className={`font-display text-xl tracking-[0.1em] ${isTransparent ? "text-[hsl(0_0%_100%)]" : "text-foreground"}`}>Capshot Visuals</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((item) => {
            const isContact = item.path === "/contact";
            const isActive = location.pathname === item.path;

            if (isContact) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-6 py-2.5 text-[10px] font-medium tracking-[0.2em] uppercase transition-all duration-300 border ${
                    isTransparent
                      ? "border-[hsl(0_0%_100%/0.4)] text-[hsl(0_0%_100%)] hover:bg-[hsl(0_0%_100%)] hover:text-[hsl(0_0%_0%)]"
                      : "border-foreground bg-foreground text-primary-foreground hover:bg-warm-gold hover:border-warm-gold"
                  }`}
                >
                  {item.label}
                </Link>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isTransparent ? "!text-[hsl(0_0%_100%/0.7)] hover:!text-[hsl(0_0%_100%)]" : ""} ${isActive ? (isTransparent ? "!text-[hsl(0_0%_100%)]" : "!text-foreground") : ""}`}
              >
                {item.label}
              </Link>
            );
          })}

          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 text-xs font-medium tracking-widest uppercase transition-colors duration-200 ${isTransparent ? "text-[hsl(0_0%_100%/0.6)]" : "text-muted-foreground hover:text-foreground"}`}
          >
            <span className={i18n.language === "en" ? (isTransparent ? "text-[hsl(0_0%_100%)]" : "text-foreground") : ""}> EN</span>
            <span className="mx-0.5 opacity-40">|</span>
            <span className={i18n.language === "hu" ? (isTransparent ? "text-[hsl(0_0%_100%)]" : "text-foreground") : ""}>HU</span>
          </button>
        </nav>

        {/* Mobile */}
        <div className="md:hidden">
          <button
            className={`p-2 ${menuOpen ? "text-foreground" : isTransparent ? "text-[hsl(0_0%_100%)]" : "text-foreground"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 bg-background z-[55]"
          >
            <div className="flex justify-end px-6 pt-6">
              <button onClick={() => setMenuOpen(false)} className="p-2 text-foreground" aria-label="Close menu">
                <X size={22} />
              </button>
            </div>
            <nav className="flex flex-col items-center pt-12 gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-display text-2xl tracking-wide ${location.pathname === item.path ? "text-foreground" : "text-muted-foreground"}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <button
                onClick={toggleLang}
                className="flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-muted-foreground mt-4"
              >
                <span className={i18n.language === "en" ? "text-foreground" : ""}>EN</span>
                <span className="mx-0.5 opacity-40">|</span>
                <span className={i18n.language === "hu" ? "text-foreground" : ""}>HU</span>
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
